import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { CocktailStatus } from '@muddle/core';

/** Status pill; colour carries meaning, the label always says it too. */
export function StatusPill({ status }: { status: CocktailStatus }) {
  const tone = { idea: 'neutral', testing: 'warning', finalized: 'positive', retired: 'neutral' }[
    status
  ];
  return <span className={`pill ${tone}`}>{status}</span>;
}

export function ErrorMessage({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="msg err" role="alert">
      {message}{' '}
      {onRetry && (
        <button className="btn btn-sm" type="button" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Skeleton({ height = 96 }: { height?: number }) {
  return <div className="skeleton" style={{ height }} aria-hidden />;
}

export function PageHeader({
  kicker,
  title,
  sub,
  actions,
}: {
  kicker: string;
  title: string;
  sub?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div className="ph-left">
        <div className="ph-kicker">{kicker}</div>
        <h1 className="ph-title">{title}</h1>
        {sub && <p className="ph-sub">{sub}</p>}
      </div>
      {actions && <div className="ph-right mu-actions">{actions}</div>}
    </div>
  );
}

/** Native <dialog> confirm: title is the question, buttons are the verbs. */
export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  danger = true,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onCancel={onCancel}
      onClose={() => {
        // A late close event from an earlier close can arrive after the dialog reopened: ignore it then.
        if (open && !ref.current?.open) onCancel();
      }}
    >
      <h2>{title}</h2>
      <p>{body}</p>
      <div className="actions">
        <button className="btn btn-ghost" type="button" onClick={onCancel}>
          Cancel
        </button>
        <span className="spacer" />
        <button
          className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
          type="button"
          onClick={onConfirm}
        >
          {confirmLabel}
        </button>
      </div>
    </dialog>
  );
}

interface ToastItem {
  id: number;
  text: string;
  error?: boolean;
}
const ToastCtx = createContext<(text: string, opts?: { error?: boolean }) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(0);
  const push = useCallback((text: string, opts?: { error?: boolean }) => {
    const id = next.current++;
    setItems((l) => [...l, { id, text, error: opts?.error }]);
    setTimeout(() => setItems((l) => l.filter((t) => t.id !== id)), 3500);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-wrap mu-toasts" role="status" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className={`toast${t.error ? ' err' : ''}`}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}
